import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import categoryService from '../services/categoryService';

const MyRequestsPage = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      active: 'success',
      rejected: 'error',
      archived: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('common.pending', 'En attente'),
      active: t('common.confirmed', 'Approuvée'),
      rejected: t('myRequests.statusRejected', 'Rejetée'),
      archived: t('myRequests.statusArchived', 'Archivée')
    };
    return labels[status] || status;
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await categoryService.getMyCategoryRequests();
      setRequests(data);
    } catch (err) {
      setError(err?.response?.data?.message || t('myRequests.loadError', 'Erreur lors du chargement de vos demandes'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const getRequestType = () => 'category_creation';

  const getRequestTypeLabel = (type) => {
    const labels = {
      category_creation: t('myRequests.categoryRequestType', 'Ajout de catégorie')
    };
    return labels[type] || type;
  };

  const filteredRequests = requests.filter((request) => {
    const requestType = getRequestType(request);
    const matchesType = typeFilter === 'all' || requestType === typeFilter;
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    return matchesType && matchesStatus;
  });

  const pendingCount = requests.filter((request) => request.status === 'pending').length;
  const approvedCount = requests.filter((request) => request.status === 'active').length;
  const rejectedCount = requests.filter((request) => request.status === 'rejected').length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'flex-start',
          mb: 2,
          py: 2,
          px: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
        {t('navigation.myRequests', 'Mes demandes')}
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 1.5, color: 'white' }}>
        {t('myRequests.subtitle', 'Suivez l\'état de vos demandes (en cours, rejetées, approuvées).')}
      </Typography>
      </Box>
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={(event, value) => setStatusFilter(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label={t('myRequests.filterAll', 'Toutes')} />
          <Tab value="pending" label={`${t('myRequests.filterPending', 'En cours')} (${pendingCount})`} />
          <Tab value="active" label={`${t('myRequests.filterApproved', 'Approuvées')} (${approvedCount})`} />
          <Tab value="rejected" label={`${t('myRequests.filterRejected', 'Rejetées')} (${rejectedCount})`} />
        </Tabs>
      </Paper>

      <FormControl sx={{ mb: 3, minWidth: 260 }}>
        <InputLabel>{t('myRequests.filterType', 'Type de demande')}</InputLabel>
        <Select
          value={typeFilter}
          label={t('myRequests.filterType', 'Type de demande')}
          onChange={(event) => setTypeFilter(event.target.value)}
        >
          <MenuItem value="all">{t('myRequests.filterAllTypes', 'Tous les types')}</MenuItem>
          <MenuItem value="category_creation">
            {t('myRequests.categoryRequestType', 'Ajout de catégorie')}
          </MenuItem>
        </Select>
      </FormControl>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><strong>{t('myRequests.type', 'Type')}</strong></TableCell>
                <TableCell><strong>{t('myRequests.name', 'Demande')}</strong></TableCell>
                <TableCell><strong>{t('myRequests.status', 'Statut')}</strong></TableCell>
                <TableCell><strong>{t('myRequests.createdAt', 'Date de demande')}</strong></TableCell>
                <TableCell align="right"><strong>{t('myRequests.actions', 'Actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('myRequests.emptyFiltered', 'Aucune demande ne correspond au filtre sélectionné.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>{getRequestTypeLabel(getRequestType(request))}</TableCell>
                    <TableCell>{request.name}</TableCell>
                    <TableCell>
                      <Chip size="small" color={getStatusColor(request.status)} label={getStatusLabel(request.status)} />
                    </TableCell>
                    <TableCell>
                      {request.createdAt ? new Date(request.createdAt).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setSelectedRequest(request)}
                        title={t('common.viewDetails', 'Voir les détails')}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(selectedRequest)}
        onClose={() => setSelectedRequest(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('myRequests.detailsTitle', 'Détails de la demande')}</DialogTitle>
        <DialogContent>
          {/* Afficher l'image de la catégorie si elle existe */}
          {selectedRequest?.imageUrl && (
            <Box
              sx={{
                mt: 1.5,
                mb: 2,
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200,
                maxHeight: 300
              }}
            >
              <Box
                component="img"
                src={selectedRequest.imageUrl}
                alt={selectedRequest.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography><strong>{t('myRequests.type', 'Type')}:</strong> {t('myRequests.categoryRequestType', 'Ajout de catégorie')}</Typography>
            <Typography><strong>{t('myRequests.name', 'Demande')}:</strong> {selectedRequest?.name || '-'}</Typography>
            <Typography><strong>{t('common.description', 'Description')}:</strong> {selectedRequest?.description || '-'}</Typography>
            <Typography><strong>{t('myRequests.status', 'Statut')}:</strong> {selectedRequest ? getStatusLabel(selectedRequest.status) : '-'}</Typography>
            <Typography>
              <strong>{t('myRequests.createdAt', 'Date de demande')}:</strong>{' '}
              {selectedRequest?.createdAt ? new Date(selectedRequest.createdAt).toLocaleDateString('fr-FR') : '-'}
            </Typography>
            {selectedRequest?.approvedAt && (
              <Typography>
                <strong>{t('myRequests.processedAt', 'Date de traitement')}:</strong>{' '}
                {new Date(selectedRequest.approvedAt).toLocaleDateString('fr-FR')}
              </Typography>
            )}
            {selectedRequest?.rejectionReason && (
              <Alert severity="error">
                <strong>{t('myRequests.rejectionReason', 'Motif de rejet')}:</strong> {selectedRequest.rejectionReason}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRequest(null)}>{t('common.close', 'Fermer')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyRequestsPage;
